import React, { useState, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { Flex, Text, Input } from '../../../common';
import { PRICING } from '../../../../constants/staticTexts';

const PricingSection = ({ 
  messagePrice, 
  onPriceChange,
  pricingLimits,
  onPriceError,
  context = 'post'
}) => {
  const theme = useTheme();
  const [priceError, setPriceError] = useState('');

  // Posts and paid messages have separate admin limits
  const isMessage = context === 'message';
  const minPrice = parseFloat((isMessage ? pricingLimits?.messageMinPrice : pricingLimits?.postMinPrice) ?? 2.99);
  const maxPrice = parseFloat((isMessage ? pricingLimits?.messageMaxPrice : pricingLimits?.postMaxPrice) ?? 150.00);

  const handlePriceChange = useCallback((value) => {
    // Always update the value so user can type
    onPriceChange(value);
    setPriceError(''); // Clear error while typing
    onPriceError?.(''); // Notify parent

    // Validate after a short delay to let user finish typing
    if (value && value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 0) {
        if (numValue < minPrice) {
          const error = `Minimum price is $${minPrice.toFixed(2)}`;
          setPriceError(error);
          onPriceError?.(error);
        } else if (numValue > maxPrice) {
          const error = `Maximum price is $${maxPrice.toFixed(2)}`;
          setPriceError(error);
          onPriceError?.(error);
        }
      }
    }
  }, [onPriceChange, minPrice, maxPrice, onPriceError]);

  return (
    <CompactPricingContainer>
      <PriceRow>
        <PricingLabel>{PRICING.MESSAGE_PRICE_LABEL}</PricingLabel>
        <PriceInputWrapper>
          <Input
            type="number"
            min={minPrice.toString()}
            step="0.01"
            placeholder={minPrice.toFixed(2)}
            value={messagePrice}
            onChange={(e) => handlePriceChange(e.target.value)}
            icon={<span style={{ fontSize: '14px', color: theme.colors.textSecondary }}>$</span>}
            error={priceError}
            marginBottom="0"
            rounded
          />
        </PriceInputWrapper>
      </PriceRow>
    </CompactPricingContainer>
  );
};

// Styled Components using common components
const CompactPricingContainer = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
`;

const PriceRow = styled(Flex)`
  align-items: center;
  gap: 12px;
`;

const PricingLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  flex-shrink: 0;
`;

const PriceInputWrapper = styled.div`
  flex: 1;
  min-width: 100px;
`;

export default PricingSection;
